import React from 'react';
import {
  BRAND_ICON_PATHS, ICON_CORAL, ICON_STROKES, ICON_TONES, ICON_VIEWBOX,
} from './brandIconPath';

/**
 * Mixtape Memories logo — Figma "05 · Assets".
 *
 *   variant="icon"        the cassette mark alone (favicons, avatars, tight spaces)
 *   variant="horizontal"  mark + "mixtape memories" wordmark (the default lockup:
 *                         site header, footer, emails, documents)
 *
 *   tone="light"  ink on paper/cream (default)
 *   tone="dark"   paper on ink (dark navigation bars, inverse panels)
 *
 * The wordmark is live text in Archivo Black — Figma's lockup uses a real text layer
 * — so it stays crisp and is read out once by assistive tech via the wrapper label.
 * Brand-book legibility floor: 36px lockup height for recurring placements, 22px for
 * tight formats; `size` is the mark height in px.
 */
const Mark = ({ size, tone, title }) => {
  const c = ICON_TONES[tone] || ICON_TONES.light;
  const [, , w, h] = ICON_VIEWBOX.split(' ').map(Number);
  return (
    <svg
      width={Math.round(size * (w / h))}
      height={size}
      viewBox={ICON_VIEWBOX}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role={title ? 'img' : undefined}
      aria-label={title || undefined}
      aria-hidden={title ? undefined : true}
      focusable="false"
    >
      <path d={BRAND_ICON_PATHS.body} stroke={c.line} strokeWidth={ICON_STROKES.body} />
      <path d={BRAND_ICON_PATHS.cut} fill={ICON_CORAL} />
      <path d={BRAND_ICON_PATHS.reelA} fill={c.reel} stroke={c.line} strokeWidth={ICON_STROKES.reel} />
      <path d={BRAND_ICON_PATHS.reelB} fill={c.reel} stroke={c.line} strokeWidth={ICON_STROKES.reel} />
    </svg>
  );
};

/*
 * `decorative`: set when the logo sits inside something that already names it
 * (e.g. a link labelled "Mixtape Memories home"), so screen readers don't hear
 * the name twice and the logo isn't listed as a separate image.
 */
const MixtapeLogo = ({ variant = 'horizontal', tone = 'light', size = 28, className = '', decorative = false }) => {
  if (variant === 'icon') {
    return (
      <span className={`inline-flex ${className}`} aria-hidden={decorative || undefined}>
        <Mark size={size} tone={tone} title={decorative ? undefined : 'Mixtape Memories logo'} />
      </span>
    );
  }
  const textColour = tone === 'dark' ? 'text-brand-white' : 'text-brand-ink';
  return (
    <span
      className={`inline-flex items-center gap-[0.55em] ${className}`}
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : 'Mixtape Memories'}
      aria-hidden={decorative || undefined}
      style={{ fontSize: Math.round(size * 0.72) }}
    >
      <Mark size={size} tone={tone} />
      <span aria-hidden="true" className={`font-display leading-none whitespace-nowrap ${textColour}`}>
        mixtape memories
      </span>
    </span>
  );
};

export default MixtapeLogo;
